import React, { useEffect, useState } from "react";
import api, { inr } from "@/lib/api";
import { toast } from "sonner";
import DetailPopup from "@/components/DetailPopup";
import WhatsAppShortcuts from "@/components/WhatsAppShortcuts";
import AdminDeleteButton from "@/components/AdminDeleteButton";
import { usePagination } from "@/components/Paginator";

export function Invoices() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [popup, setPopup] = useState(null);
  const load = async () => setItems((await api.get("/invoices")).data);
  useEffect(() => { load(); }, []);
  const shown = q ? items.filter(i => [i.invoice_no,i.customer_name].join(" ").toLowerCase().includes(q.toLowerCase())) : items;
  const { pageItems, controls } = usePagination(shown, 20);
  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="label-tiny mb-1">Billing</div>
          <h1 className="font-display text-3xl tracking-tight">Invoices</h1>
          <p className="text-sm text-[#757169] mt-1">Sequential per Indian FY. Format: <span className="mono">MA{`{FY}`}/{`{n}`}</span></p>
        </div>
        <input className="input-field w-56 max-w-full" placeholder="Search invoice/customer…" value={q} onChange={e=>setQ(e.target.value)} data-testid="invoice-search"/>
      </div>
      <div className="card-surface overflow-x-auto">
        <table className="data-table">
          <thead><tr>
            <th>Invoice #</th><th>Customer</th><th>FY</th><th>Total</th><th>Paid</th><th>Status</th><th>Actions</th>
          </tr></thead>
          <tbody>
            {shown.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-[#757169]">No invoices yet — open an order and click &ldquo;Generate invoice&rdquo;.</td></tr>}
            {pageItems.map(i => (
              <tr key={i.id} data-testid={`invoice-row-${i.id}`} className="cursor-pointer" onClick={()=>setPopup(i)}>
                <td className="mono font-semibold">{i.invoice_no}</td>
                <td>{i.customer_name}</td>
                <td className="mono">{i.fy}</td>
                <td className="mono">{inr(i.total)}</td>
                <td className="mono">{inr(i.amount_paid)}</td>
                <td><span className={`badge ${i.status==="paid"?"badge-success":i.status==="partial"?"badge-warn":"badge-neutral"}`}>{i.status}</span></td>
                <td onClick={e=>e.stopPropagation()}>
                  <div className="flex gap-1.5 flex-wrap items-center">
                    <a href={`/print/invoice/${i.id}`} target="_blank" rel="noreferrer" className="btn-ghost text-xs">Print</a>
                    {i.customer_phone && i.status !== "paid" && (
                      <WhatsAppShortcuts phone={i.customer_phone} name={i.customer_name} variant="compact" testid={`wa-invoice-${i.id}`} />
                    )}
                    <AdminDeleteButton endpoint={`/invoices/${i.id}`} label={`invoice ${i.invoice_no}`} onDone={load} testid={`delete-invoice-${i.id}`} />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {controls && <div className="px-2 pb-3">{controls}</div>}
      </div>

      <DetailPopup open={!!popup} onClose={()=>setPopup(null)} subtitle="Invoice" title={popup?.invoice_no || ""}
        actions={popup && <>
          <a href={`/print/invoice/${popup.id}`} target="_blank" rel="noreferrer" className="btn-ghost text-xs">Print / PDF</a>
          {popup.customer_phone && <WhatsAppShortcuts phone={popup.customer_phone} name={popup.customer_name} variant="compact" />}
        </>}>
        {popup && <>
          <div><span className="text-[#75706A]">Customer:</span> <b>{popup.customer_name}</b>{popup.customer_phone && <span> · <span className="mono">{popup.customer_phone}</span></span>}</div>
          <div><span className="text-[#75706A]">FY:</span> <span className="mono">{popup.fy}</span> · <span className="text-[#75706A]">Status:</span> <span className={`badge ${popup.status==="paid"?"badge-success":popup.status==="partial"?"badge-warn":"badge-neutral"}`}>{popup.status}</span></div>
          <div className="mt-2 pt-2 border-t border-[#E7E1D3] grid grid-cols-2 gap-2 text-xs">
            <div><span className="text-[#75706A]">Subtotal:</span> <span className="mono">{inr(popup.subtotal || 0)}</span></div>
            <div><span className="text-[#75706A]">GST:</span> <span className="mono">{inr(popup.gst_amount || 0)}</span></div>
            <div><span className="text-[#75706A]">Total:</span> <span className="mono font-bold">{inr(popup.total || 0)}</span></div>
            <div><span className="text-[#75706A]">Paid:</span> <span className="mono">{inr(popup.amount_paid || 0)}</span></div>
            <div className="col-span-2"><span className="text-[#75706A]">Balance:</span> <span className={`mono font-bold ${Number(popup.total || 0) - Number(popup.amount_paid || 0) > 0 ? "text-[#B83A3A]" : "text-[#4A7258]"}`}>{inr(Number(popup.total || 0) - Number(popup.amount_paid || 0))}</span></div>
          </div>
          <div className="text-xs text-[#75706A] mt-1">Created: {popup.created_at?.slice(0,10)}</div>
        </>}
      </DetailPopup>
    </div>
  );
}

export function Payments() {
  const [items, setItems] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [popup, setPopup] = useState(null);
  const [form, setForm] = useState({ invoice_id: "", amount: 0, method: "upi", reference: "" });

  const load = async () => {
    setItems((await api.get("/payments")).data);
    setInvoices((await api.get("/invoices")).data.filter(i => i.status !== "paid"));
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    const inv = invoices.find(i => i.id === form.invoice_id);
    if (!inv) return toast.error("Pick an invoice");
    try {
      await api.post("/payments", { ...form, order_id: inv.order_id });
      toast.success("Payment recorded");
      setForm({ invoice_id: "", amount: 0, method: "upi", reference: "" });
      load();
    } catch(e){ toast.error(e.response?.data?.detail || "Failed"); }
  };

  const invById = (id) => (invoices.find(x => x.id === id));
  const allInvoices = React.useMemo(() => items.map(p => ({ p, i: invById(p.invoice_id) })), [items, invoices]); // eslint-disable-line
  const { pageItems, controls } = usePagination(items, 20);

  return (
    <div className="space-y-6">
      <div>
        <div className="label-tiny mb-1">Money in</div>
        <h1 className="font-display text-3xl tracking-tight">Payments</h1>
      </div>

      <form onSubmit={submit} className="card-surface p-5 grid grid-cols-1 md:grid-cols-5 gap-3" data-testid="payment-form">
        <div className="md:col-span-2">
          <div className="label-tiny mb-1.5">Against invoice</div>
          <select className="input-field" value={form.invoice_id} onChange={e=>setForm({...form, invoice_id: e.target.value})} required data-testid="payment-invoice">
            <option value="">— select —</option>
            {invoices.map(i => <option key={i.id} value={i.id}>{i.invoice_no} · {i.customer_name} · {inr(i.total - i.amount_paid)} due</option>)}
          </select>
        </div>
        <div><div className="label-tiny mb-1.5">Amount</div>
          <input type="number" step="0.01" required className="input-field" value={form.amount} onChange={e=>setForm({...form,amount:Number(e.target.value)})} data-testid="payment-amount"/></div>
        <div><div className="label-tiny mb-1.5">Method</div>
          <select className="input-field" value={form.method} onChange={e=>setForm({...form,method:e.target.value})}>
            <option value="upi">UPI</option><option value="bank">Bank transfer</option><option value="cash">Cash</option>
          </select></div>
        <div><div className="label-tiny mb-1.5">Reference</div>
          <input className="input-field" value={form.reference} onChange={e=>setForm({...form,reference:e.target.value})} placeholder="txn id / cheque"/></div>
        <div className="md:col-span-5 flex justify-end"><button className="btn-primary" data-testid="payment-submit">Record payment</button></div>
      </form>

      <div className="card-surface overflow-x-auto">
        <table className="data-table">
          <thead><tr><th>Invoice</th><th>Amount</th><th>Method</th><th>Reference</th><th>Received</th><th></th></tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-[#757169]">No payments yet.</td></tr>}
            {pageItems.map(p => (
              <tr key={p.id} className="cursor-pointer" onClick={()=>setPopup(p)}>
                <td className="mono text-xs">{p.invoice_id?.slice(0,8)}</td>
                <td className="mono">{inr(p.amount)}</td>
                <td>{p.method}</td>
                <td className="text-xs">{p.reference || "—"}</td>
                <td className="mono text-xs">{p.received_at?.slice(0,10)}</td>
                <td onClick={e=>e.stopPropagation()}>
                  <AdminDeleteButton endpoint={`/payments/${p.id}`} label={`payment ${inr(p.amount)}`} onDone={load} testid={`delete-payment-${p.id}`} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {controls && <div className="px-2 pb-3">{controls}</div>}
      </div>

      <DetailPopup open={!!popup} onClose={()=>setPopup(null)} subtitle="Payment" title={popup ? inr(popup.amount) : ""}>
        {popup && <>
          <div><span className="text-[#75706A]">Method:</span> {popup.method}</div>
          <div><span className="text-[#75706A]">Reference:</span> <span className="mono">{popup.reference || "—"}</span></div>
          <div><span className="text-[#75706A]">Against invoice:</span> <span className="mono">{popup.invoice_id?.slice(0,12)}</span></div>
          <div><span className="text-[#75706A]">Received at:</span> <span className="mono">{popup.received_at?.slice(0,19)?.replace("T"," ")}</span></div>
        </>}
      </DetailPopup>
    </div>
  );
}
